import s from './MediaCard.module.sass'

import React, { FC, HTMLProps, useMemo } from 'react'
import Link from 'next/link'

import { Favorite } from '@features/Favorite'

import { ChevronWithTooltip } from '@shared/ui/Feedback/ChevronWithTooltip'
import { ErrorBoundary } from '@shared/lib/components/ErrorBoundary'
import { IconButton } from '@shared/ui/Actions/IconButton'
import { PickAnalytic } from '@shared/api/analytics/types/types'
import { Rating } from '@shared/ui/Feedback/Rating'
import { Text } from '@shared/ui/Typography/Text'
import { TypeMappingNewsOutput } from '@shared/api/middleware/mappingAPI/news/type'
import { TypeMappingSelectionOutput } from '@shared/api/middleware/mappingAPI/selection/type'
import { useClientResponsive } from '@shared/lib/hooks/useClientResponsive'
import { useCurrentSize } from '@shared/lib/helpers/classes'

import IconSelectionChevronMedium from '@icons/icon-selection-chevron--medium.svg'
import IconLike from '@icons/icon-like.svg'
import { MediaImg } from '@shared/ui/Media/MediaImg'
import { ImageModel } from '@shared/ui/Media/MediaImg/types/types'

// 1x1, 2x1 for mobile
// type size = 'L1x1 L2x1 M1x1 M2x1 S1x1 1x1 2x1'
interface Props {
  id: string | number
  sizes: string
  title: string
  type?: {
    name: string
    url?: string
  }
  date?: string
  img: {
    desktop: {
      horizontal: string
      vertical: string
    }
    mobile: {
      vertical: string
    }
  }
  url: string
  favorite: {
    category: 'news' | 'selection'
    analytic: {
      data: TypeMappingNewsOutput | TypeMappingSelectionOutput
      other: PickAnalytic<'section_name'>
    }
  }
  promo?: boolean
  chevron?: boolean
  className?: string
}

const sizesImage = {
  L1x1: { width: 297 * 2, height: 297 * 2 },
  L2x1: { width: 610 * 2, height: 297 * 2 },
  M1x1: { width: 219 * 2, height: 219 * 2 },
  M2x1: { width: 453 * 2, height: 219 * 2 },
  S1x1: { width: 141 * 2, height: 141 * 2 },
  '1x1': { width: 158 * 2, height: 158 * 2 },
  '2x1': { width: 324 * 2, height: 158 * 2 },
}

const MediaCard: FC<Props & HTMLProps<HTMLDivElement>> = React.forwardRef(
  (
    { id, sizes, title, type, date, img, favorite, url = '', promo = false, chevron = false, className = '', ...props },
    ref,
  ) => {
    const [size] = useCurrentSize(sizes)
    const { isMobile } = useClientResponsive()
    const isLongFormat = size === 'L2x1' || size === 'M2x1' || size === '2x1'

    const isShowType = type.name

    const getPhotoLink = useMemo((): ImageModel => {
      const result = {
        Url: null,
        Width: null,
        Height: null,
        Crop: null,
        Description: null,
        Copyright: null,
        Author: null,
      }

      if (isMobile) {
        result.Url = img.mobile.vertical
      } else {
        result.Url = isLongFormat ? img.desktop.horizontal : img.desktop.vertical
      }

      result.Width = sizesImage[size].width
      result.Height = sizesImage[size].height

      return result
    }, [isLongFormat, img])

    return (
      <ErrorBoundary>
        <div data-id={id} className={`${s.card} ${className}`} data-size={size} title={title} {...props} ref={ref}>
          <div className={s['image-wrapper']}>
            {promo && <Rating className={s.promo} mode={'fill'} isPromo sizes={'S'} />}

            {!!chevron && (
              <ChevronWithTooltip icon={IconSelectionChevronMedium} className={s['afisha-selection-chevron--medium']} />
            )}

            <Favorite
              category={favorite.category}
              id={Number(id)}
              analytic={{ data: favorite.analytic.data, other: favorite.analytic.other }}
              className={s.favorite}>
              <IconButton sizes={'S'} mode={'white'} icon={IconLike} />
            </Favorite>

            <Link href={url} className={s['card-image-link']} scroll={false}>
              <MediaImg
                image={getPhotoLink}
                width={sizesImage[size].width}
                height={sizesImage[size].height}
                className={s.image}
                flexible={true}
              />
            </Link>
          </div>

          <div className={s['card-extra']}>
            <>
              {date && (
                <Text className={s['card-date']} sizes={'S'}>
                  {date}
                </Text>
              )}

              {date && isShowType && <span className={s['card-dot']}>·</span>}

              {type.name &&
                (type.url ? (
                  <Link href={type.url} legacyBehavior scroll={false}>
                    <Text className={s['card-media-type']} sizes={'S'}>
                      {type.name}
                    </Text>
                  </Link>
                ) : (
                  <Text className={s['card-media-type']} sizes={'S'}>
                    {type.name}
                  </Text>
                ))}
            </>
          </div>

          <div className={s['card-description']}>
            <Link href={url} scroll={false}>
              <Text sizes={'M M M S'}>{title}</Text>
            </Link>
          </div>
        </div>
      </ErrorBoundary>
    )
  },
)

MediaCard.displayName = 'MediaCard'

export default MediaCard
